import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Phone, Check, Star, ShieldCheck, Clock, Sparkles } from "lucide-react";
import { SERVICES, TESTIMONIALS, HERO_IMG, COMPANY, ZONES } from "@/data/content";
import { Section, Overline, usePageTitle } from "@/components/site/ui-bits";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C {...p} />;
};

export default function Home() {
  usePageTitle("Entreprise de nettoyage à Cahors & Cazals");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Overline>Equinoxe Propreté</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              La propreté qui<br />
              <span className="text-[#0ea5e9]">illumine</span> vos espaces
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-600 max-w-lg">
              Ménage, entretien intérieur et extérieur, remise en état, débarras et bien plus.
              Une équipe locale à votre service à Cahors, Cazals et leurs alentours.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-button" className="inline-flex items-center gap-2 bg-[#0ea5e9] hover:bg-[#0284c7] text-white px-7 py-3.5 rounded-full font-bold transition-colors duration-200">
                Demander un devis gratuit <ArrowRight size={18} />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} data-testid="hero-phone-button" className="inline-flex items-center gap-2 border-2 border-slate-200 hover:border-[#0ea5e9] text-slate-800 px-7 py-3.5 rounded-full font-bold transition-colors duration-200">
                <Phone size={18} /> {COMPANY.phone}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck size={18} className="text-sky-500" /> Filiale du {COMPANY.group}</span>
              <span className="flex items-center gap-2"><Clock size={18} className="text-sky-500" /> WhatsApp 24h/24</span>
              <span className="flex items-center gap-2"><Star size={18} className="text-sky-500" /> Particuliers & professionnels</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
            <img src={HERO_IMG} alt="Intérieur lumineux et propre" className="rounded-3xl shadow-[0_30px_60px_rgba(14,165,233,0.18)] w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 hidden sm:flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center"><Sparkles className="text-[#0ea5e9]" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">100%</p>
                <p className="text-xs text-slate-500">Satisfaction propreté</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CREDIT D'IMPOT */}
      <Section className="!py-16">
        <div className="max-w-5xl mx-auto rounded-3xl border-2 border-sky-100 bg-sky-50/50 p-8 lg:p-12 flex flex-col md:flex-row items-center gap-8">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/c0df428a-f30d-408b-9fd0-f73c669b11e7/images/2840defdd37ce485b25186e384fedb1c5bffd5a30a24bd810752842c6d907726.jpeg"
            alt="50% de crédit d'impôt"
            className="w-32 h-32 flex-shrink-0 rounded-full"
            data-testid="credit-impot-badge"
          />
          <div className="text-center md:text-left">
            <Overline>Agréé Service à la Personne</Overline>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Payez seulement la moitié, immédiatement
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Equinoxe Propreté est un organisme <b>agréé Services à la Personne</b>. Nos prestations à
              domicile pour les particuliers ouvrent droit à un <b>crédit d'impôt de 50 %</b>. Grâce à
              l'<b>Avance Immédiate</b>, vous ne réglez que <b>50 % de la facture, tout de suite</b> —
              sans attendre le remboursement fiscal.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Renseignez-vous auprès de Gabriel (bulle de chat en bas à droite) ou auprès d'un de nos
              conseillers au <b>05 32 18 90 44</b>.
            </p>
          </div>
        </div>
      </Section>

      {/* SERVICES */}
      <Section>
        <div className="text-center max-w-2xl mx-auto">
          <Overline>Nos prestations</Overline>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Des services pensés pour chaque besoin</h2>
          <p className="mt-4 text-slate-600">Du ménage régulier à la remise en état complète, nous prenons soin de vos espaces.</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link to={`/services#${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block h-full bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl p-7 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(14,165,233,0.12)] transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center text-[#0ea5e9] group-hover:bg-[#0ea5e9] group-hover:text-white transition-colors duration-300">
                  <Icon name={s.icon} size={22} />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.cardShort || s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0ea5e9]">En savoir plus <ArrowRight size={15} /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* WHY US */}
      <section className="bg-[#0ea5e9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-8">
          {[
            { icon: "Users", t: "Une équipe locale", d: "Basés dans le Lot, nous connaissons votre région et intervenons rapidement." },
            { icon: "BadgeCheck", t: "Travail soigné", d: "Minutie, discrétion et produits adaptés pour un résultat impeccable." },
            { icon: "Building2", t: "Particuliers & pros", d: "Résidences, locations saisonnières, agences immobilières, locaux d'entreprise." },
          ].map((b) => (
            <div key={b.t} className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white"><Icon name={b.icon} /></div>
              <h3 className="mt-5 text-lg font-semibold text-white">{b.t}</h3>
              <p className="mt-2 text-sm text-white/85">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Section>
        <div className="text-center max-w-2xl mx-auto">
          <Overline>Témoignages</Overline>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Ils nous font confiance</h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} data-testid="testimonial-card" className="bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl p-6">
              <div className="flex gap-0.5 text-amber-400 mb-3">
                {Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={15} fill="currentColor" />)}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">“{t.text}”</p>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                <p className="text-xs text-slate-500">{t.type} · {t.city}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ZONES STRIP */}
      <section className="bg-[#0ea5e9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold">Le Lot et les départements limitrophes</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            {ZONES.slice(0, 12).map((z) => (
              <span key={z} className="bg-white/15 backdrop-blur px-4 py-1.5 rounded-full text-sm">{z}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <Section className="text-center">
        <div className="max-w-3xl mx-auto bg-slate-900 rounded-3xl p-12 lg:p-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Besoin d'un espace impeccable ?</h2>
          <p className="mt-4 text-slate-300">Recevez un devis gratuit et sans engagement sous 48h.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-quote-bottom" className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white px-8 py-3.5 rounded-full font-bold transition-colors">Demander un devis</Link>
            <a href={`tel:${COMPANY.phoneRaw}`} className="border-2 border-white/30 hover:border-white text-white px-8 py-3.5 rounded-full font-bold transition-colors inline-flex items-center gap-2"><Phone size={18} /> {COMPANY.phone}</a>
          </div>
        </div>
      </Section>
    </div>
  );
}
